import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form7004Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 7004 Instructions | Business Tax Extension - TaxZerone®"
          description="Get IRS Form 7004 instructions here. File for business tax extensions with ease. Follow step-by-step guidance for e-filing. Deadline and penalties explained."
          keywords="form 7004 instructions, instructions for form 7004, form 7004 instruction, instructions form 7004, irs form 7004 instructions, form 7004 irs instructions, instructions for irs form 7004, form 7004 filing instructions, form 7004 extension instructions, form 7004 instructions where to file"
          canonical="resources/tax-extension/form-7004-instructions/"
          ogType="article"
          jsonLdPricing="17.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 7004</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Get extra time to file. Submit your Form 7004 online today.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004instructions`}
          >
            Get an Extension Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-7004-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 7004 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Form 7004</span> is
                        filed by businesses with the IRS to request an
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          automatic extension of up to 6 months
                        </span>{' '}
                        to file eligible business tax returns.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="/resources/tax-extension/form-7004-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>
                        : Form 7004 must be filed{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          on or before the original due date
                        </span>{' '}
                        of the return.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                        Late filing penalty applies unless you show a reasonable
                        cause, late payment penalty of{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          0.5% per month (up to 25%)
                        </span>
                        , and applicable interest.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7004, Application for Automatic Extension of Time To File
                  Certain Business Income Tax, Information, and Other Returns,
                  is filed by businesses in the United States to get an
                  extension for filing certain business income taxes.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  By filing{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    IRS Form 7004
                  </Link>
                  , businesses can get up to 6 months of extension. Also,
                  businesses don’t need to provide a reason for corporate tax
                  extension filing.
                </Typography>
                <Box sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }} />
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What’s New:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For tax year 2025, filers must submit a{' '}
                  <span style={{ fontWeight: 600 }}> paper Form 7004</span> to
                  request an extension of time to file{' '}
                  <span style={{ fontWeight: 600 }}>
                    Forms 708, 8924, and 8928.
                  </span>{' '}
                  Electronic filing for these forms will become available
                  starting in{' '}
                  <span style={{ fontWeight: 600 }}>tax year 2026.</span>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Electronic Payments{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  The IRS strongly encourages taxpayers to make payments
                  electronically whenever possible. You can pay using{' '}
                  <span style={{ fontWeight: 600 }}>
                    Direct Pay from your bank account,
                  </span>{' '}
                  a{' '}
                  <span style={{ fontWeight: 600 }}>debit or credit card,</span>{' '}
                  a <span style={{ fontWeight: 600 }}>digital wallet,</span> or
                  your{' '}
                  <span style={{ fontWeight: 600 }}>IRS Online Account.</span>{' '}
                  To review all available payment methods, visit IRS.gov/Pay.
                  Additional details are provided in the instructions for{' '}
                  <span style={{ fontWeight: 600 }}>Part II, line 8.</span>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 7004?
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  Most businesses that want extra time to file their business
                  income tax return can{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 7004
                  </Link>
                  . Below business types can use Form 7004 extension to file
                  business income tax:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>C-corporations (Form 1120)</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      S-corporations (
                      <Link
                        href="/resources/business-tax/form-1120-s-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1120S
                      </Link>
                      )
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Partnerships (
                      <Link
                        href="/resources/business-tax/form-1065-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1065
                      </Link>
                      )
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Multiple-member LLCs as corporation and partnership
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Trust, Estates and Others (Form 1041)
                    </ListItemText>
                  </ListItem>
                </List>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If your business is required to file Form
                  1041-A, Trust Accumulation of Charitable Amounts, and you want
                  an extension of time, you will need to{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8868-exempt-organization-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 8868
                  </Link>
                  .
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 7004 due date?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For the 2025 tax year, the deadline for filing Form 7004 is
                  March 16, 2026.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Businesses that want an extension can file 7004 form on or
                  before the{' '}
                  <Link
                    href="/resources/tax-extension/form-7004-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    due date
                  </Link>{' '}
                  for the actual return to be filed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  For corporations, the due date to file income tax returns is
                  the 15th day of the third month after the tax year-end. If a
                  corporation follows the regular calendar year, i.e., January
                  to December, the deadline to file the income tax return is
                  March 15. However, if the due date falls on weekends or
                  federal holidays, then the filing deadline will be the next
                  business day.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Want to file Form 7004 for your business before the
                        deadline?
                      </Typography>
                      <Typography variant="body2">
                        E-file Form 7004 with the IRS using TaxZerone. Complete
                        the whole filing process by following 3 simple steps in
                        less than 5 minutes.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 7004 penalty
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.8,
                  }}
                >
                  Penalty for late filing of return:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If a tax return is filed after the due date, including any
                  approved extension, the IRS may charge a{' '}
                  <span style={{ fontWeight: 600 }}> late filing penalty</span>{' '}
                  unless the taxpayer can show a reasonable cause for the delay.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.8,
                  }}
                >
                  Penalty for late payment of tax:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    A{' '}
                    <span style={{ fontWeight: 600 }}>
                      late payment penalty
                    </span>{' '}
                    may also apply if taxes are not paid by the original due
                    date. Generally, the penalty is{' '}
                    <span style={{ fontWeight: 600 }}>
                      0.5% of the unpaid tax for each month
                    </span>{' '}
                    or part of a month the tax remains unpaid, up to a{' '}
                    <span style={{ fontWeight: 600 }}>
                      maximum of 25% of the total tax due.
                    </span>{' '}
                    This penalty may be waived if the taxpayer can show
                    reasonable cause for the delay in payment.{' '}
                  </li>
                  <li>
                    If a corporation receives an extension to file its income
                    tax return, a late payment penalty will not apply if{' '}
                    <span style={{ fontWeight: 600 }}>
                      at least 90% of the total tax liability
                    </span>{' '}
                    (reported on Part II, line 6) is paid by the original due
                    date, and the remaining balance is paid by the extended
                    filing deadline.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.8,
                  }}
                >
                  Interest:
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Interest is{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    charged on any unpaid tax
                  </span>{' '}
                  from the original due date of the return until the tax is
                  fully paid. Interest applies even if the taxpayer has been
                  granted an extension or has reasonable cause for not paying on
                  time.
                </Typography>
                {/* <Typography variant="body1" sx={{ mb: 3 }}>
                  If you miss filing your business income tax and paying the tax
                  due within the deadline, you will incur penalties from the
                  IRS. Below are the penalty rates for Form 7004:
                </Typography>

                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Failure to pay taxes and file tax returns
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Filed tax return or extension form, but failed to
                            pay taxes
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            5% of the tax due per month, up to 5 months.
                          </TableCell>
                          <TableCell>
                            0.5% of the tax due per month, up to 25%
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            If your return is not filed for more than 60 days
                            after the deadline, there will be a minimum penalty
                            of $135.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 7004 Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Follow the instructions below to fill out Form 7004.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f7004-instruction-img.webp" />
                  <source srcSet="/assets/images/f7004-instruction-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f7004-instruction-img.png"
                    loading="lazy"
                    alt="Form 7004 – Enter the Name, EIN, and full address"
                    width={760}
                    height={194}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Name:</b> Enter the filer’s name. The name entered here
                  should match the name on the IRS database.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>EIN:</b> Enter the 9-digit employer identification number.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Address:</b> : Enter the business address. If the business
                  address is outside the United States, enter the address along
                  with the country name
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part I: Automatic Extension for Certain Business Income Tax,
                  Information, and Other Return
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f7004-part1-img.webp" />
                  <source srcSet="/assets/images/f7004-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f7004-part1-img.png"
                    loading="lazy"
                    alt="Form 7004 – Part I: Enter the applicable Form Code for the return"
                    width={760}
                    height={340}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 3,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 1:</b> Enter the form code based on the type of return
                  for which you are requesting an extension.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part II: All Filers Must Complete This Part
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f7004-part2-img.webp" />
                  <source srcSet="/assets/images/f7004-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f7004-part2-img.png"
                    loading="lazy"
                    alt="Form 7004 – Part II: Select the boxes that are applicable. Enter the required tax and total payments details. "
                    width={760}
                    height={283}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 2:</b> Check this box if your organization is a
                  foreign corporation and does not have an office location or
                  address in the United States.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 3:</b> Check this box if your organization is a
                  corporation and is the common parent of a group that intends
                  to file a consolidated return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  After checking the box, add the list of consolidated group
                  members’ information, such as name, EIN, and address.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 4:</b> Check this box if your organization comes under
                  Regulations section 1.6081-5. Businesses under Regulations
                  section 1.6081-5 get an automatic extension to file income tax
                  returns.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If filed, these businesses will get an additional extension of
                  time to file the tax return, and they are required to pay the
                  tax dues by 6th month following the tax year-end.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> The additional extension period for this type of
                  business is.
                  <List sx={{ mb: 0, pb: 0 }}>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0 }}>
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText
                        sx={{
                          '& span': { fontSize: '14px !important' },
                        }}
                      >
                        3 months for partnerships and S corporations{' '}
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText
                        sx={{
                          '& span': { fontSize: '14px !important' },
                        }}
                      >
                        4 months for C corporations and filers of Form
                        1120-POL.{' '}
                      </ListItemText>
                    </ListItem>
                  </List>
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 5a:</b> Select your tax year. If you follow a
                  different tax year from the regular calendar year, enter the
                  same.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 5b:</b> Check the reason for the short tax year.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 2, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If the reason is “Change in accounting period”,
                  the business must have already got approval for the tax year
                  change.
                  <br />
                  <br /> If your reason is not listed, check the “Other” box and
                  enter the reason for the short tax year.
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 6:</b> Enter the total tax amount for the tax year,
                  which includes any nonrefundable credits.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 7:</b> Enter the payments made in the tax year, which
                  include refundable credits.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 4,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Line 8:</b> Subtract line 7 from line 6 and enter the
                  balance due amount.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 7004?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 7004 can be filed with the IRS either electronically or
                  by paper filing.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic filing or E-filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  By e-filing business tax extension, you get a lot of benefits,
                  such as quick filing time and instant return status. To e-file
                  Form 7004, you will need to first choose an e-file service
                  provider.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  TaxZerone supports the e-filing of Form 7004, and it is also
                  an IRS-authorized e-file service provider. You can simply
                  enter the required information, calculate the balance due, and
                  transmit the return to the IRS.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can also file Form 7004 with the IRS by manually filling
                  out the form and sending it by postal email. If you are paper
                  filing extension forms, make sure you are sending your returns
                  earlier before the deadline, as it may take some time for the
                  return to reach the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Although there are two filing options available for businesses
                  to file extension, the IRS recommends the e-filing of Form
                  7004 as it is simple, easy, and takes less time and cost
                  compared to paper filing.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Choose E-filing. Choose TaxZerone.
                      </Typography>
                      <Typography variant="body2">
                        Save time, money, and effort when you file Form 7004
                        online with TaxZerone, an IRS-authorized e-file
                        provider.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to mail Form 7004?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  After filling out Form 7004, you are required to manually send
                  the return to the IRS by postal mail. The address to which you
                  need to send the returns will vary based on your location.
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check out the Form 7004 mailing address based on the business
                  location.
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            IF the form is
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            <Hidden mdDown>
                              AND the settler is (or was at death)
                              <br />
                            </Hidden>{' '}
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            THEN file Form 7004 at:
                            <br />
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                            OR E-file Form 7004 using
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            708 <br /> 706-GS(T)
                            <br /> 706-GS(D)
                          </TableCell>
                          <TableCell>
                            A resident U.S. citizen, resident alien, nonresident
                            U.S. citizen, or alien
                          </TableCell>
                          <TableCell>
                            Department of the Treasury Internal Revenue Service
                            Kansas City, MO 64999-0019
                          </TableCell>
                          <TableCell>
                            TaxZerone At{' '}
                            <span style={{ fontWeight: 600 }}>
                              {' '}
                              just $17.99/form
                            </span>{' '}
                            [Except for 706-GS(D)]
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box component="div" sx={{ mb: 5 }}>
                    <TableContainer sx={{ border: '1px solid #828282' }}>
                      <Table sx={{}} aria-label="Due Date Chart">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#ccc' }}>
                            <TableCell sx={{ color: '#444', width: 300 }}>
                              IF the form is
                              <Hidden mdDown>
                                <br />
                              </Hidden>{' '}
                            </TableCell>

                            <TableCell sx={{ color: '#444', width: 300 }}>
                              <Hidden mdDown>
                                AND your principal business, office, or agency
                                is located in ...
                                <br />
                              </Hidden>{' '}
                            </TableCell>

                            <TableCell sx={{ color: '#444', width: 300 }}>
                              THEN file Form 7004 at:
                              <br />
                            </TableCell>

                            <TableCell sx={{ color: '#444', width: 300 }}>
                              <Hidden mdDown>
                                <br />
                              </Hidden>{' '}
                              OR E-file Form 7004 using
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow sx={{}}>
                            <TableCell>
                              1041
                              <br /> 1120-H
                            </TableCell>
                            <TableCell>
                              Connecticut, Delaware, District of Columbia,
                              Georgia, Illinois, Indiana, Kentucky, Maine,
                              Maryland, Massachusetts, Michigan, New Hampshire,
                              New Jersey, New York, North Carolina,
                              Ohio,Pennsylvania, Rhode Island, South Carolina,
                              Tennessee, Vermont, Virginia, West Virginia,
                              Wisconsin
                            </TableCell>
                            <TableCell>
                              Department of the Treasury Internal Revenue
                              Service Kansas City, MO 64999-0019
                            </TableCell>
                            <TableCell>
                              E-File with TaxZerone in 3 simple steps
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell>
                              1041
                              <br /> 1120-H
                            </TableCell>
                            <TableCell>
                              Alabama, Alaska, Arizona, Arkansas, California,
                              Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                              Louisiana, Minnesota, Mississippi, Missouri,
                              Montana, Nebraska, Nevada, New Mexico, North
                              Dakota, Oklahoma, Oregon, South Dakota, Texas,
                              Utah, Washington, Wyoming
                            </TableCell>
                            <TableCell>
                              Department of the Treasury Internal Revenue
                              Service Ogden, UT 84201-0045
                            </TableCell>
                            <TableCell>
                              Complete your filing with TaxZerone in less than 5
                              minutes
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{}}>
                            <TableCell>
                              1041 <br /> 1120-H
                            </TableCell>
                            <TableCell>
                              A foreign country or U.S. territory
                            </TableCell>
                            <TableCell>
                              {' '}
                              Internal Revenue Service P.O. Box 409101 Ogden, UT
                              84409
                            </TableCell>
                            <TableCell>
                              File with TaxZerone and stay in compliance with
                              the IRS
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell>
                              1041-QFT
                              <br /> 8725
                              <br /> 8831
                              <br /> 8876 <br />
                              8924 <br />
                              8928
                            </TableCell>
                            <TableCell>Any Location</TableCell>
                            <TableCell>
                              Department of the Treasury Internal Revenue
                              Service Kansas City, MO 64999-0019
                            </TableCell>
                            <TableCell>
                              TaxZerone - an IRS authorized service provider
                              (Except for 8725, 8831, 8876, 8924, and 8928)
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{}}>
                            <TableCell>
                              1042
                              <br /> 1120-F
                              <br /> 1120-FSC <br />
                              3520-A
                              <br /> 8804
                            </TableCell>
                            <TableCell>Any Location</TableCell>
                            <TableCell>
                              Internal Revenue Service P.O. Box 409101 Ogden, UT
                              84409
                            </TableCell>
                            <TableCell>
                              TaxZerone supports quick and secure filing
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell>
                              1066
                              <br /> 1120-C <br />
                              1120-PC
                            </TableCell>
                            <TableCell>The United States</TableCell>
                            <TableCell>
                              Department of the Treasury Internal Revenue
                              Service Ogden, UT 84201-0045
                            </TableCell>
                            <TableCell>
                              E-File with TaxZerone and receive instant filing
                              status updates
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{}}>
                            <TableCell>
                              1066
                              <br /> 1120-C <br />
                              1120-PC
                            </TableCell>
                            <TableCell>
                              A foreign country or U.S. territory
                            </TableCell>
                            <TableCell>
                              Internal Revenue Service P.O. Box 409101 Ogden, UT
                              84409
                            </TableCell>
                            <TableCell>
                              Submit your extension online with TaxZerone
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell>
                              1041-N
                              <br /> 1120-POL
                              <br /> 1120-L
                              <br /> 1120-ND
                              <br /> 1120-SF
                            </TableCell>
                            <TableCell>Any location</TableCell>
                            <TableCell>
                              Department of the Treasury Internal Revenue
                              Service Ogden, UT 84409-0045
                            </TableCell>
                            <TableCell>
                              File your extension confidently with TaxZerone
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{}}>
                            <TableCell>
                              1065
                              <br /> 1120 <br />
                              1120-REIT
                              <br /> 1120-RIC <br />
                              1120-S
                              <br /> 8612
                              <br /> 8613
                            </TableCell>

                            <TableCell>
                              Connecticut, Delaware, District of Columbia,
                              Georgia, Illinois, Indiana, Kentucky, Maine,
                              Maryland, Massachusetts, Michigan, New Hampshire,
                              New Jersey, New York, North Carolina, Ohio,
                              Pennsylvania, Rhode Island, South Carolina,
                              Tennessee, Vermont, Virginia, West Virginia,
                              Wisconsin
                            </TableCell>
                            <TableCell>
                              And the total assets at the end of the tax year
                              are: Less than $10 million: Department of the
                              Treasury Internal Revenue Service Kansas City, MO
                              64999-0019; $10 million or more: Department of the
                              Treasury Internal Revenue Service Ogden, UT
                              84201-0045
                            </TableCell>
                            <TableCell>
                              TaxZerone makes e-filing simple (Except for 8612
                              and 8613)
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                            <TableCell>
                              1065
                              <br /> 1120
                              <br /> 1120-REIT
                              <br /> 1120-RIC
                              <br /> 1120-S
                              <br /> 8612
                              <br /> 8613
                            </TableCell>

                            <TableCell>
                              Alabama, Alaska, Arizona, Arkansas, California,
                              Colorado, Florida, Hawaii, Idaho, Iowa, Kansas,
                              Louisiana, Minnesota, Mississippi, Missouri,
                              Montana, Nebraska, Nevada, New Mexico, North
                              Dakota, Oklahoma, Oregon, South Dakota, Texas,
                              Utah, Washington, Wyoming
                            </TableCell>
                            <TableCell>
                              Department of the Treasury Internal Revenue
                              Service Ogden, UT 84201-0045
                            </TableCell>
                            <TableCell>
                              Simplify your extension filing in under 5 minutes
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{}}>
                            <TableCell>
                              1065
                              <br /> 1120
                              <br /> 1120-L
                              <br /> 1120-ND
                              <br /> 1120-REIT
                              <br /> 1120-RIC
                              <br /> 1120-S
                              <br />
                              1120-SF
                              <br /> 8612
                              <br /> 8613
                            </TableCell>

                            <TableCell>
                              A foreign country or U.S. territory
                            </TableCell>
                            <TableCell>
                              Internal Revenue Service P.O. Box 409101 Ogden, UT
                              84409
                            </TableCell>
                            <TableCell>
                              Get your extension submitted instantly with
                              TaxZerone
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> With TaxZerone, you can easily{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1120-s/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1120-S
                  </Link>
                  and{' '}
                  <Link
                    href="/business-tax/efile-irs-form-1065-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 1065
                  </Link>{' '}
                  for the 2025 tax year, helping S Corporations and Partnerships
                  stay accurate and on time
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 7004 online?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing Form 7004 online can be completed in 3 simple steps.
                  Before starting to e-file, keep the information below ready.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Information required to file Form 7004 electronically
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Filer information such as name, EIN, and address
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      List of consolidated group members, if your organization
                      is a corporation and is the common parent of a group that
                      intends to file a consolidated return
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Tax years</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Tentative total tax</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Total payments and credits</ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When you have all the information, you can then follow the
                  steps below to{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-7004-business-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 7004
                  </Link>{' '}
                  using TaxZerone.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 1:</b> Select the tax year and enter the required
                  information listed above.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 2:</b> Calculate the balance due by providing
                  tentative total tax and total payments and credits.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 3:</b> Transmit the return to the IRS.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Do you have the required information, and do you find the
                      steps simple?
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 7004 with TaxZerone and get an extension of up
                      to 6 months.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004instructions`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to pay the balance due while filing Form 7004 online?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  While completing step 2, if you have a balance due, you can
                  choose to pay the due amount using the{' '}
                  <Link
                    href="/resources/electronic-funds-withdrawal-efw/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    electronic funds withdrawal
                  </Link>{' '}
                  (EFW) option. You will need to provide your bank account
                  information, such as account number, account type, and routing
                  number.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Once the IRS processes your return, your balance due will be
                  automatically debited from your account. Make sure to maintain
                  a sufficient balance in your account.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 7004 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your Form 7004 easily with TaxZerone at just
              <b> $17.99/return.</b> You will only have to enter the required
              information, calculate your balance due, and transmit the return
              to the IRS.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              You will also get updates on your return status as soon as the IRS
              processes it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file Form 7004 online?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=7004instructions`}
              >
                E-file Form 7004 Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Takes 3 steps and less than 5 minutes
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form7004Instructions;
